import { createContext, useContext, useState, useCallback, PropsWithChildren } from 'react';

export interface OverlayItem {
	title: string;
	bullets: string[];
}

export interface ToastData {
	id: string;
	headline: string;
	message: string;
	ctaText?: string;
	overlayTitle?: string;
	overlayMainText?: string;
	overlayItems?: OverlayItem[];
	status?: 'success' | 'error' | 'warning' | 'info';
	variant?: 'reply';
	replyLabel?: string;
	confirmLabel?: string;
	onReply?: () => void;
	onConfirm?: () => void;
}

interface OverlayState {
	isOpen: boolean;
	title: string | null;
	mainText: string | null;
	items: OverlayItem[];
	toastId: string | null;
}

interface StatusToastContextType {
	addToast: (toast: Omit<ToastData, 'id'>) => void;
	removeToast: (id: string) => void;
	toasts: ToastData[];
	openOverlay: (title: string, mainText: string, items: OverlayItem[], toastId: string) => void;
	closeOverlay: () => void;
	overlay: OverlayState;
}

const StatusToastContext = createContext<StatusToastContextType | undefined>(undefined);

export const useStatusToastContext = () => {
	const context = useContext(StatusToastContext);
	if (!context) {
		throw new Error('useStatusToastContext must be used within StatusToastProvider');
	}
	return context;
};

export const StatusToastProvider = ({ children }: PropsWithChildren<unknown>) => {
	const [toasts, setToasts] = useState<ToastData[]>([]);
	const [overlay, setOverlay] = useState<OverlayState>({
		isOpen: false,
		title: null,
		mainText: null,
		items: [],
		toastId: null,
	});

	const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
		const id = `toast-${Date.now()}-${Math.random()}`;
		setToasts((prev) => [...prev, { ...toast, id }]);
	}, []);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	const openOverlay = useCallback((title: string, mainText: string, items: OverlayItem[], toastId: string) => {
		setOverlay({
			isOpen: true,
			title,
			mainText,
			items,
			toastId,
		});
	}, []);

	const closeOverlay = useCallback(() => {
		setOverlay({
			isOpen: false,
			title: null,
			mainText: null,
			items: [],
			toastId: null,
		});
	}, []);

	return (
		<StatusToastContext.Provider value={{ 
			addToast, 
			removeToast, 
			toasts,
			openOverlay,
			closeOverlay,
			overlay,
		}}>
			{children}
		</StatusToastContext.Provider>
	);
};

